import React, { useState, useEffect, useRef, useContext } from "react";
import {
  FiSearch,
  FiFilter,
  FiMessageSquare,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiSend,
  FiPaperclip,
  FiSmile,
  FiX,
  FiMapPin,
  FiChevronLeft,
} from "react-icons/fi";
import { BsThreeDots } from "react-icons/bs";
import "../../../assets/css/admin/dashboard/admindispute/disputemanagement.css";
import AdminMasterLayout from "layouts/AdminMasterLayout";
import AppContext from "context/AppContext";
import axios from "axios";
import { time } from "framer-motion";


/* ─── Helpers ──────────────────────────────────────────────── */
const priorityCls = (p) => `dsp-priority--${p.toLowerCase()}`;
const statusCls = (s) => `dsp-status--${s.toLowerCase()}`;

/* ══════════════════════════════════════════════════════════════
   DETAILS PANEL
══════════════════════════════════════════════════════════════ */
const DetailsPanel = ({
  dispute,
  onClose,
  onUpdate,
  isMobile,
  baseUrlForFiles,
}) => {
  const [activeTab, setActiveTab] = useState("property");
  const [reply, setReply] = useState("");
  const [messages, setMessages] = useState([]);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const chatEndRef = useRef(null);
  const { baseUrl, authToken } = useContext(AppContext);

  const fetchConversation = (conversationId) => {
    if (!conversationId) {
      setMessages([]);
      return;
    }

    axios
      .post(
        `${baseUrl}/admin/helpandsupport/fetchConversation`,
        {
          exconversation_id: conversationId,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      )
      .then((response) => {
        if (response.data.success === true) {
          setMessages(response.data.conversation?.exchats || []);
        } else {
          setMessages([]);
        }
      })
      .catch((error) => {
        console.log("Fetch conversation error:", error);
        setMessages([]);
      });
  };

  const sendAdminMessage = () => {
    const text = reply.trim();
    const conversationId = dispute?.exconversation?.id;

    if (!text) return;

    if (!conversationId) {
      alert("Conversation is not available for this support request.");
      return;
    }

    axios
      .post(
        `${baseUrl}/admin/helpandsupport/sendReply`,
        {
          exconversation_id: conversationId,
          exhelpsupport_id: dispute.id,
          message: text,
          sender: "exadmin",
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      )
      .then((response) => {
        console.log("Send message response:", response.data);

        if (response.data.success === true) {
          setReply("");

          fetchConversation(
            response.data.conversation_id || conversationId
          );
        }
      })
      .catch((error) => {
        console.log("Send admin message error:", error);
      });
  };
  const handleFileSelect = (e) => {
    const file = e.target.files[0];

    if (file) {
      setSelectedFile(file);
      console.log("Selected File:", file);
    }
  };
  useEffect(() => {
    setReply("");
    setMessages([]);

    if (dispute?.exconversation?.id) {
      fetchConversation(dispute.exconversation.id);
    }
  }, [dispute]);
  /* scroll to bottom on new message */
  useEffect(() => {
    if (activeTab === "chat") {
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({
          behavior: "smooth",
        });
      }, 50);
    }
  }, [messages, activeTab]);

  /* reset tab when dispute changes */
  useEffect(() => {
    setActiveTab("property");
    setReply("");
  }, [dispute?.id]);



  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendAdminMessage();
    }
  };

  const closeDispute = () => {
    if (window.confirm(`Close dispute ${dispute.id}?`)) {
      onUpdate({ ...dispute, status: "Closed" });
      onClose();
    }
  };

  const getProfileImage = (msg) => {
    if (msg.sender === "exadmin") {
      return null;
    }

    if (msg.sender === "exbuyer") {
      const buyer = dispute.against === "buyer" ? dispute.against_buyer : dispute.exbuyers;
      return buyer?.profileImage ? `${baseUrlForFiles}/${buyer.profileImage}` : "/default-profile.png";
    }

    if (msg.sender === "exseller") {
      const seller = dispute.against === "seller" ? dispute.against_seller : dispute.exsellers;
      return seller?.profileImage ? `${baseUrlForFiles}/${seller.profileImage}` : "/default-profile.png";
    }
    return "/default-profile.png";
  };

  return (
    <div className="dsp-panel">
      {/* Panel Header */}
      <div className="dsp-panel__header">
        <div className="dsp-panel__header-top">
          {isMobile && (
            <button className="dsp-icon-btn dsp-back-btn" onClick={onClose}>
              <FiChevronLeft size={18} />
            </button>
          )}
          <h3 className="dsp-panel__title">Dispute Details</h3>
          {!isMobile && (
            <button
              className="dsp-icon-btn dsp-round-btn"
              onClick={onClose}
              title="Close"
            >
              <FiX size={15} />
            </button>
          )}
        </div>
        <div className="dsp-panel__meta">
          <span className="dsp-panel__id">{dispute.id}</span>
          <span className={`dsp-badge ${statusCls(dispute.status)}`}>
            {dispute.status}
          </span>
          <span className={`dsp-badge ${priorityCls(dispute.priority)}`}>
            {dispute.priority}
          </span>
        </div>
      </div>

      {/* Property Snapshot */}
      <div className="dsp-panel__property">
        <img
          src={`${baseUrlForFiles}/${dispute.exproperties?.expropertyimages?.[0]?.images}`}
          alt={dispute.exproperties?.property_title}
          className="dsp-panel__prop-img"
        />

        <div className="dsp-panel__prop-info">
          <p className="dsp-panel__prop-name">
            {dispute.exproperties?.property_title}
          </p>

          <p className="dsp-panel__prop-loc">
            {dispute.exproperties?.property_location}
          </p>
        </div>
      </div>
      {/* <div className="dsp-panel__property">
        <img
          src={dispute.property.img}
          alt={dispute.property.name}
          className="dsp-panel__prop-img"
        />
        <div className="dsp-panel__prop-info">
          <p className="dsp-panel__prop-name">{dispute.property.name}</p>
          <p className="dsp-panel__prop-expert">
            <FiMessageSquare size={11} /> {dispute.property.expert}
          </p>
          <p className="dsp-panel__prop-loc">
            <FiMapPin size={11} /> {dispute.property.location}
          </p>
        </div>
      </div> */}

      {/* Tabs */}
      <div className="dsp-panel__tabs">
        <button
          className={`dsp-panel__tab ${activeTab === "property" ? "dsp-panel__tab--active" : ""}`}
          onClick={() => setActiveTab("property")}
        >
          Property Details
        </button>
        <button
          className={`dsp-panel__tab ${activeTab === "chat" ? "dsp-panel__tab--active" : ""}`}
          onClick={() => setActiveTab("chat")}
        >
          Chat History
          {messages?.length > 0 && (
            <span className="dsp-panel__tab-badge">
              {messages.length}
            </span>
          )}
        </button>
      </div>

      {/* Tab Body */}
      <div className="dsp-panel__body">
        {/* ── Property Tab ── */}
        {activeTab === "property" && (
          <div className="dsp-prop-info">
            {[
              [
                "Raised By",
                dispute.exbuyers
                  ? `${dispute.exbuyers?.firstName || ""} ${dispute.exbuyers?.lastName || ""} (Buyer)`
                  : dispute.exsellers
                    ? `${dispute.exsellers?.firstName || ""} ${dispute.exsellers?.lastName || ""} (Seller)`
                    : "N/A",
              ],

              [
                "Against",
                dispute.against === "seller"
                  ? dispute.against_seller
                    ? `${dispute.against_seller.firstName || ""} ${dispute.against_seller.lastName || ""} (Seller)`
                    : "N/A"
                  : dispute.against === "buyer"
                    ? dispute.against_buyer
                      ? `${dispute.against_buyer.firstName || ""} ${dispute.against_buyer.lastName || ""} (Buyer)`
                      : "N/A"
                    : "N/A",
              ],

              ["Category", dispute.category],
              ["Date Filed", new Date(dispute.created_at).toLocaleDateString()],
              ["Location", dispute.exproperties?.location],
            ].map(([label, val]) => (
              <div className="dsp-prop-info__row" key={label}>
                <span className="dsp-prop-info__label">{label}</span>
                <span className="dsp-prop-info__value">{val}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Chat Tab ── */}
        {activeTab === "chat" && (
          <div className="dsp-chat">
            <div className="dsp-chat__messages">
              {!messages?.length ? (
                <p className="dsp-chat__empty">
                  No messages yet. Start the conversation.
                </p>
              ) : (
                messages.map((msg) => {
                  const isAdmin = msg.sender === "exadmin";

                  return (
                    <div
                      key={msg.id}
                      className={`dsp-chat__msg ${isAdmin ? "dsp-chat__msg--admin" : "dsp-chat__msg--user"
                        }`}
                    >
                      {/* Left avatar */}
                      {!isAdmin && (
                        <img
                          src={getProfileImage(msg)}
                          className="dsp-chat-avatar-img"
                          alt=""
                        />
                      )}

                      <div
                        className={`dsp-chat__bubble ${isAdmin
                          ? "dsp-chat__bubble--admin"
                          : "dsp-chat__bubble--user"
                          }`}
                      >
                        <div className="dsp-chat__meta">
                          <span className="dsp-chat__sender">
                            {msg.sender === "exbuyer"
                              ? "Buyer"
                              : msg.sender === "exseller"
                                ? "Seller"
                                : "Admin"}
                          </span>

                          <span className="dsp-chat__time">
                            {new Date(msg.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>

                        <p className="dsp-chat__text">{msg.massage}</p>
                      </div>

                      {/* Right avatar */}
                      {isAdmin && (
                        <div className="dsp-chat__avatar dsp-chat__avatar--admin">
                          AD
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Reply Box */}
            <div className="dsp-reply">
              <textarea
                className="dsp-reply__input"
                placeholder="Type your reply… (Enter to send)"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={handleKey}
                rows={2}
              />
              {selectedFile && (
                <div className="selected-file">📎 {selectedFile.name}</div>
              )}
              <div className="dsp-reply__footer">
                <div className="dsp-reply__icons">
                  <button
                    className="dsp-reply__icon-btn"
                    title="Attach"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <FiPaperclip size={15} />
                  </button>

                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                  />

                  {/* <button className="dsp-reply__icon-btn" title="Emoji">
                    <FiSmile size={15} />
                  </button> */}
                </div>
                <button
                  className={`dsp-reply__send ${reply.trim() ? "dsp-reply__send--active" : ""}`}
                  onClick={sendAdminMessage}
                  disabled={!reply.trim()}
                >
                  <FiSend size={13} /> Send
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="dsp-panel__actions">
        <button className="dsp-panel__btn dsp-panel__btn--secondary">
          Request Info
        </button>
        <button
          className="dsp-panel__btn dsp-panel__btn--danger"
          onClick={closeDispute}
        >
          <FiX size={13} /> Close Dispute
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
const DisputeManagement = () => {
  const [disputes, setDisputes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCat] = useState("All");
  const [statusFilter, setStatus] = useState("All");
  const [visibleStats, setVis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const { baseUrl, baseUrlForFiles, authToken } = useContext(AppContext);
  /* responsive check */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const getAgainstUser = (dispute) => {
    if (dispute.against === "seller") {
      return dispute.responder || dispute.againstSeller || null;
    }

    if (dispute.against === "buyer") {
      return dispute.responder || dispute.againstBuyer || null;
    }

    return null;
  };

  /* stagger stat cards */
  // useEffect(() => {
  //   STATS.forEach((s, i) =>
  //     setTimeout(() => setVis((p) => [...p, s.id]), i * 110),
  //   );
  // }, []);

  /* keep selected synced after update */
  useEffect(() => {
    if (selected) {
      const updated = disputes.find((d) => d.id === selected.id);
      if (updated) setSelected(updated);
    }
  }, [disputes]);

  const categories = ["All", ...new Set(disputes.map((d) => d.category))];

  const filtered = disputes.filter((d) => {
    const q = search.toLowerCase();

    const raisedByName = d.exbuyers
      ? `${d.exbuyers?.firstName || ""} ${d.exbuyers?.lastName || ""}`
      : d.exsellers
        ? `${d.exsellers?.firstName || ""} ${d.exsellers?.lastName || ""}`
        : "";

    const againstUser =
      d.against === "seller"
        ? d.against_seller
        : d.against === "buyer"
          ? d.against_buyer
          : null;

    const againstName = againstUser
      ? `${againstUser?.firstName || ""} ${againstUser?.lastName || ""}`
      : "";

    const propertyName = d.exproperties?.property_title || "";

    const matchQ =
      !q ||
      String(d.id).toLowerCase().includes(q) ||
      raisedByName.toLowerCase().includes(q) ||
      againstName.toLowerCase().includes(q) ||
      propertyName.toLowerCase().includes(q) ||
      (d.category || "").toLowerCase().includes(q);

    return (
      matchQ &&
      (catFilter === "All" || d.category === catFilter) &&
      (statusFilter === "All" || d.status === statusFilter)
    );
  });

 const handleUpdate = (updated) => {
  setDisputes((prev) =>
    prev.map((d) =>
      d.id === updated.id ? updated : d
    )
  );
};

  const handleRowClick = (dispute) => {
    setSelected((prev) => (prev?.id === dispute.id ? null : dispute));
  };

  // Handle priority change
 const handlePriorityChange = (disputeId, newPriority, e) => {
  e.stopPropagation();

  setDisputes((prev) =>
    prev.map((d) =>
      d.id === disputeId
        ? { ...d, priority: newPriority }
        : d
    )
  );

  console.log(
    `Priority changed for dispute ${disputeId} to ${newPriority}`
  );
};

  /* mobile: show panel fullscreen, hide table */
  const showTable = !isMobile || !selected;
  const showPanel = !!selected;

  const viewAllHelpandSupport = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/helpandsupport/viewAllHelpandSupport`, {
        headers: {
          "Content-Type": "multipart/formdata",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        console.log(response.data.Allhelpandsupport);
        setDisputes(response.data.Allhelpandsupport);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };
  useEffect(() => {
    viewAllHelpandSupport();
  }, []);
  return (
    <AdminMasterLayout pageTitle="Help and Support">
      <div className="dsp-page">
        {/* ── Page Title (hide on mobile when panel open) ── */}
        {/* {showTable && (
          <div className="dsp-page__header">
            <h1 className="dsp-page__title">Dispute Management</h1>
          </div>
        )} */}

        {/* ── Stats ── */}
        {/* {showTable && (
          <div className="dsp-stats">
            {STATS.map((s, i) => (
              <div
                key={s.id}
                className={`dsp-stat ${visibleStats.includes(s.id) ? "dsp-stat--visible" : ""}`}
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="dsp-stat__icon">
                  <s.Icon size={22} />
                </div>
                <div className="dsp-stat__body">
                  <p className="dsp-stat__label">{s.label}</p>
                  <p className="dsp-stat__value">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
        )} */}

        {/* ── Main Layout ── */}
        <div className={`dsp-layout ${showPanel ? "dsp-layout--split" : ""}`}>
          {/* ── Table Column ── */}
          {showTable && (
            <div className="dsp-table-col">
              {/* Section Header */}
              <div className="dsp-table-col__header">
                <h2 className="dsp-table-col__title">Recent Disputes</h2>
                <span className="dsp-count-chip">
                  {filtered.length} disputes
                </span>
              </div>

              {/* Filters */}
              <div className="dsp-filters">
                {/* <select
                  className="dsp-select"
                  value={catFilter}
                  onChange={(e) => setCat(e.target.value)}
                >
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c === "All" ? "All Categories" : c}
                    </option>
                  ))}
                </select> */}

                {/* <select
                  className="dsp-select"
                  value={statusFilter}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="All">All Status</option>
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                </select> */}

                <div className="dsp-search">
                  <FiSearch className="dsp-search__icon" />
                  <input
                    type="text"
                    className="dsp-search__input"
                    placeholder="Search disputes…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      className="dsp-search__clear"
                      onClick={() => setSearch("")}
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>

                {/* <button className="dsp-icon-btn" title="More">
                  <BsThreeDots />
                </button>
                <button className="dsp-icon-btn" title="Filter">
                  <FiFilter />
                </button> */}
              </div>

              {/* Table */}
              <div className="dsp-table-wrap">
                <table className="dsp-table">
                  <thead>
                    <tr>
                      <th>Dispute ID</th>
                      <th>Raised By</th>
                      <th>Against</th>
                      <th>Property</th>
                      <th>Category</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="dsp-table-loading-row">
                          <div className="dsp-table-loading">
                            <div className="dsp-table-loader"></div>
                            <p>Loading help and support requests...</p>
                          </div>
                        </td>
                      </tr>
                    ) : filtered.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="dsp-table__empty">
                          No disputes found.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((d, idx) => (
                        <tr
                          key={d.id}
                          className={`dsp-table__row ${selected?.id === d.id ? "dsp-table__row--active" : ""}`}
                          style={{ animationDelay: `${idx * 0.05}s` }}
                          onClick={() => handleRowClick(d)}
                        >
                          <td>
                            <span className="dsp-id-chip">{d.id}</span>
                          </td>
                          {/* <td>
                        <div className="dsp-user">
                          <div className="dsp-avatar">{d.raisedBy.avatar}</div>
                          <div>
                            <p className="dsp-user__name">{d.exbuyer?.firstName}</p>
                            <p className="dsp-user__role">{d.raisedBy.role}</p>
                          </div>
                        </div>
                      </td> */}
                          <td>
                            <div className="dsp-user">
                              <img
                                src={
                                  d.exbuyers?.profileImage
                                    ? `${baseUrlForFiles}/${d.exbuyers.profileImage}`
                                    : d.exsellers?.profileImage
                                      ? `${baseUrlForFiles}/${d.exsellers.profileImage}`
                                      : "/default-profile.png"
                                }
                                alt="Profile"
                                className="dsp-avatar-img"
                              />

                              <div>
                                <p className="dsp-user__name">
                                  {d.exbuyers
                                    ? `${d.exbuyers?.firstName || ""} ${d.exbuyers?.lastName || ""}`
                                    : d.exsellers
                                      ? `${d.exsellers?.firstName || ""} ${d.exsellers?.lastName || ""}`
                                      : "N/A"}
                                </p>

                                <p className="dsp-user__role">
                                  {d.exbuyers ? "Buyer" : d.exsellers ? "Seller" : "N/A"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td>
                            {(() => {
                              const againstUser =
                                d.against === "seller"
                                  ? d.against_seller
                                  : d.against_buyer;

                              return (
                                <div className="dsp-user">
                                  <img
                                    src={
                                      againstUser?.profileImage
                                        ? `${baseUrlForFiles}/${againstUser.profileImage}`
                                        : "/default-profile.png"
                                    }
                                    alt="Profile"
                                    className="dsp-avatar-img"
                                  />

                                  <div>
                                    <p className="dsp-user__name">
                                      {againstUser
                                        ? `${againstUser.firstName || ""} ${againstUser.lastName || ""
                                          }`.trim()
                                        : "N/A"}
                                    </p>

                                    <p className="dsp-user__role">
                                      {d.against === "seller"
                                        ? "Seller"
                                        : d.against === "buyer"
                                          ? "Buyer"
                                          : "N/A"}
                                    </p>
                                  </div>
                                </div>
                              );
                            })()}
                          </td>
                          <td>
                            <div className="dsp-prop-cell">
                              <img
                                src={`${baseUrlForFiles}/${d.exproperties?.expropertyimages?.[0]?.images}`}
                                alt={d.exproperties?.property_title}
                                className="dsp-prop-cell__img"
                              />
                              <span className="dsp-prop-cell__name">
                                {d.exproperties?.property_title}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="dsp-cat-badge">{d.category}</span>
                          </td>
                          <td>
                            <select
                              className={`dsp-priority-select ${priorityCls(d.priority)}`}
                              value={d.priority}
                              onChange={(e) =>
                                handlePriorityChange(d.id, e.target.value, e)
                              }
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="Low">Low</option>
                              <option value="Medium">Medium</option>
                              <option value="High">High</option>
                            </select>
                          </td>
                          <td>
                            <span
                              className={`dsp-badge ${statusCls(d.status)}`}
                            >
                              {d.status}
                            </span>
                          </td>
                          <td className="dsp-table__date">
                            {new Date(d.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Details Panel ── */}
          {showPanel && (
            <DetailsPanel
              baseUrlForFiles={baseUrlForFiles}
              dispute={selected}
              onClose={() => setSelected(null)}
              onUpdate={handleUpdate}
              isMobile={isMobile}
            />
          )}

          {/* ── Placeholder (desktop, no selection) ── */}
          {!isMobile && !selected && (
            <div className="dsp-placeholder">
              <FiMessageSquare size={48} className="dsp-placeholder__icon" />
              <p className="dsp-placeholder__text">
                Click a row to view dispute details
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminMasterLayout>
  );
};

export default DisputeManagement;
